export function formatMoney(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Number(value) || 0)
}

export function titleCase(value = '') {
  return value.replace(/-/g, ' ').replace(/\b\w/g, (match) => match.toUpperCase())
}

export function countdownParts(value, now) {
  const target = new Date(value)
  const diff = Number.isNaN(target.getTime()) ? 0 : Math.max(0, target.getTime() - now)
  const totalSeconds = Math.floor(diff / 1000)
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  return {
    days: String(days).padStart(2, '0'),
    hours: String(hours).padStart(2, '0'),
    minutes: String(minutes).padStart(2, '0'),
    seconds: String(seconds).padStart(2, '0'),
    expired: diff <= 0,
  }
}

export function auctionReserveMet(auction) {
  return auction.reservePrice <= 0 || auction.reserveMet || auction.currentBid >= auction.reservePrice
}

export function resolveAuctionStatus(auction, now = Date.now()) {
  const status = auction?.status
  const startsAt = auction?.startsAt ? new Date(auction.startsAt).getTime() : NaN
  const endsAt = auction?.endsAt ? new Date(auction.endsAt).getTime() : NaN

  if (status === 'scheduled' && Number.isFinite(startsAt) && startsAt <= now) {
    return Number.isFinite(endsAt) && endsAt <= now ? 'closed' : 'live'
  }

  if (status === 'live' && Number.isFinite(endsAt) && endsAt <= now) {
    return 'closed'
  }

  return status || 'closed'
}

export function auctionClock(auction, now) {
  const status = resolveAuctionStatus(auction, now)

  if (status === 'scheduled') {
    return {
      label: 'Starts in',
      parts: countdownParts(auction.startsAt, now),
    }
  }

  if (status === 'live') {
    return {
      label: 'Closes in',
      parts: countdownParts(auction.endsAt, now),
    }
  }

  return {
    label: auction.status === 'won' ? 'Won lot' : auction.status === 'live' ? 'Finalizing' : 'Closed lot',
    parts: countdownParts(null, now),
  }
}

export function auctionCanBid(auction, now = Date.now()) {
  const endsAt = auction?.endsAt ? new Date(auction.endsAt).getTime() : NaN

  return resolveAuctionStatus(auction, now) === 'live'
    && Number.isFinite(endsAt)
    && endsAt > now
    && Number(auction.nextBid) > 0
}

export function resolveBidReadiness(auction, user) {
  if (!auctionCanBid(auction)) {
    return {
      ready: false,
      code: 'auction-closed',
      title: 'Auction not taking bids',
      message: 'This lot is not currently accepting bids.',
      actionLabel: 'View auction rules',
      actionPath: null,
    }
  }

  if (!user) {
    return {
      ready: false,
      code: 'signed-out',
      title: 'Sign in required',
      message: 'Create or access your OBC account before entering the bidding room.',
      actionLabel: 'Sign in',
      actionPath: '/login',
    }
  }

  if (!user.verifiedBidder) {
    return {
      ready: false,
      code: 'unverified',
      title: 'Bidder verification required',
      message: 'A verified bidder card is required before live-style bids can be submitted.',
      actionLabel: 'Verify in payments',
      actionPath: '/dashboard/payments',
    }
  }

  if (!user.savedPaymentMethods?.length) {
    return {
      ready: false,
      code: 'missing-card',
      title: 'Saved card required',
      message: 'Add a primary card before bidding so wins and bundle checkout can be handled cleanly.',
      actionLabel: 'Add payment method',
      actionPath: '/dashboard/payments',
    }
  }

  return {
    ready: true,
    code: 'ready',
    title: 'Bidder ready',
    message: 'Your verified bidder card is active for this auction.',
    actionLabel: null,
    actionPath: null,
  }
}

export function auctionEndsSoon(auction, now, hours = 24) {
  if (resolveAuctionStatus(auction, now) !== 'live') return false

  const target = new Date(auction.endsAt)
  if (Number.isNaN(target.getTime())) return false

  const diff = target.getTime() - now
  return diff > 0 && diff <= hours * 60 * 60 * 1000
}

export function auctionActionLabel(auction) {
  const status = resolveAuctionStatus(auction)

  if (status === 'scheduled') return 'Watch Lot'
  if (status === 'won') return 'View Result'
  if (status === 'closed') return auction.status === 'live' ? 'Finalizing' : 'Closed'
  return 'Place Next Bid'
}

export function filterAuctionsByFocus(auctions, focus, now) {
  if (focus === 'featured') {
    return auctions.filter((auction) => auction.featured)
  }

  if (focus === 'ending-soon') {
    return auctions.filter((auction) => auctionEndsSoon(auction, now))
  }

  if (focus === 'reserve-open') {
    return auctions.filter((auction) => !auctionReserveMet(auction))
  }

  return auctions
}

export function listingInventoryLabel(listing) {
  if (listing.inventory <= 0) return 'Sold out'
  if (listing.inventory === 1) return '1 left'
  if (listing.inventory <= 2) return `${listing.inventory} left`
  return `${listing.inventory} available`
}

export function resolveShopActionReadiness(listing, user, kind) {
  const isBundle = kind === 'bundle'

  if (listing.inventory <= 0) {
    return {
      ready: false,
      code: 'sold-out',
      title: 'Sold out',
      message: 'This listing is no longer available for checkout or weekly hold.',
      actionLabel: null,
      actionPath: null,
    }
  }

  if (isBundle && !listing.bundleEligible) {
    return {
      ready: false,
      code: 'bundle-unavailable',
      title: 'Weekly bundle unavailable',
      message: 'This item must ship direct and cannot be added to the Friday bundle.',
      actionLabel: null,
      actionPath: null,
    }
  }

  if (!isBundle && !listing.quickCheckout) {
    return {
      ready: false,
      code: 'checkout-unavailable',
      title: 'Quick checkout unavailable',
      message: 'This listing requires standard order handling from the OBC team.',
      actionLabel: null,
      actionPath: null,
    }
  }

  if (!user) {
    return {
      ready: false,
      code: 'signed-out',
      title: 'Sign in required',
      message: isBundle
        ? 'Sign in before adding items to your weekly bundle.'
        : 'Sign in before using quick checkout.',
      actionLabel: 'Sign in',
      actionPath: '/login',
    }
  }

  if (!isBundle && !user.savedPaymentMethods?.length) {
    return {
      ready: false,
      code: 'missing-card',
      title: 'Saved card required',
      message: 'Add a primary card before quick checkout can run.',
      actionLabel: 'Add payment method',
      actionPath: '/dashboard/payments',
    }
  }

  return {
    ready: true,
    code: 'ready',
    title: isBundle ? 'Bundle ready' : 'Checkout ready',
    message: isBundle
      ? 'This item can be held for Friday combined checkout.'
      : 'Your saved card is ready for quick checkout.',
    actionLabel: null,
    actionPath: null,
  }
}

export function shopActionIsAvailable(readiness) {
  return Boolean(readiness?.ready || readiness?.actionPath)
}

export function sortShopListings(items, sort) {
  const copy = [...items]

  if (sort === 'price-low') {
    return copy.sort((a, b) => (a.price || 0) - (b.price || 0))
  }

  if (sort === 'price-high') {
    return copy.sort((a, b) => (b.price || 0) - (a.price || 0))
  }

  if (sort === 'low-stock') {
    return copy.sort((a, b) => (a.inventory || 0) - (b.inventory || 0))
  }

  return copy.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
}
